import { Body, Button, FlexBox, FormError, Headline, Message } from '@Backlot-Cars/archie';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import PaymentMethodCandidate from './PaymentMethodCandidate';
import PaymentMethodSelect from './PaymentMethodSelect';
import PaymentMethodsLoading from './PaymentMethodsLoading';
import PaymentMethodsUnavailable from './PaymentMethodsUnavailable';
import { getCandidateId, getInitialPaymentMethodSelection, isEligibleCandidate } from './helpers';

export {
	getCandidateDisplayName,
	getInitialPaymentMethodSelection,
	isEligibleCandidate,
	PAYMENT_METHOD_TYPES,
} from './helpers';

const PAYMENT_METHOD_STATUS = {
	processing: 'processing',
	ready: 'ready',
};

const getUnavailableSourcesMessage = (unavailableSources, t) => {
	const unavailableSourcesLabel = unavailableSources
		.map((source) => t(`signup:auctionAccessLookup.paymentMethodsStep.sources.${source}`))
		.join(', ');

	return t('signup:auctionAccessLookup.paymentMethodsStep.sourcesUnavailable', {
		unavailableSources: unavailableSourcesLabel,
	});
};

const AuctionAccessPaymentMethod = ({
	error,
	isSubmitting = false,
	onBack,
	onContinue,
	onRetry,
	onSelectionChange,
	paymentMethods,
}) => {
	const { t } = useTranslation(['signup']);
	const initialSelection = getInitialPaymentMethodSelection(paymentMethods);
	const [selectedFloorPlanCandidateIds, setSelectedFloorPlanCandidateIds] = useState(
		initialSelection.selectedCandidateIds,
	);
	const {
		clearErrors,
		control,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm({
		defaultValues: {
			defaultBuyingPaymentMethodId: initialSelection.buyingCandidateId,
			defaultSellingPaymentMethodId: initialSelection.sellingCandidateId,
		},
	});
	const defaultBuyingPaymentMethodId = watch('defaultBuyingPaymentMethodId');
	const defaultSellingPaymentMethodId = watch('defaultSellingPaymentMethodId');

	const bankAccountCandidates = useMemo(
		() =>
			(Array.isArray(paymentMethods?.bankAccountCandidates) ? paymentMethods.bankAccountCandidates : []).filter(
				isEligibleCandidate,
			),
		[paymentMethods?.bankAccountCandidates],
	);
	const floorPlanCandidates = useMemo(
		() =>
			(Array.isArray(paymentMethods?.floorPlanCandidates) ? paymentMethods.floorPlanCandidates : []).filter(
				isEligibleCandidate,
			),
		[paymentMethods?.floorPlanCandidates],
	);
	const floorPlanCandidateIds = useMemo(
		() =>
			floorPlanCandidates
				.filter((candidate) => selectedFloorPlanCandidateIds.includes(getCandidateId(candidate)))
				.map(getCandidateId),
		[floorPlanCandidates, selectedFloorPlanCandidateIds],
	);
	const selectedCandidateIds = useMemo(
		() => [
			...new Set(
				[defaultBuyingPaymentMethodId, defaultSellingPaymentMethodId, ...floorPlanCandidateIds].filter(Boolean),
			),
		],
		[defaultBuyingPaymentMethodId, defaultSellingPaymentMethodId, floorPlanCandidateIds],
	);
	const hasBankAccountCandidates = bankAccountCandidates.length > 0;
	const hasFloorPlanCandidates = floorPlanCandidates.length > 0;
	const hasEligibleCandidates = hasBankAccountCandidates || hasFloorPlanCandidates;
	// unavailableSources identifies payment-data providers that could not be queried successfully.
	const unavailableSources =
		Array.isArray(paymentMethods?.unavailableSources) ? paymentMethods.unavailableSources : [];

	useEffect(() => {
		onSelectionChange?.({
			buyingCandidateId: defaultBuyingPaymentMethodId,
			selectedCandidateIds,
			sellingCandidateId: defaultSellingPaymentMethodId,
		});
	}, [defaultBuyingPaymentMethodId, onSelectionChange, selectedCandidateIds, defaultSellingPaymentMethodId]);

	const handleFloorPlanCandidateChange = (candidate, isSelected) => {
		const candidateId = getCandidateId(candidate);
		setSelectedFloorPlanCandidateIds((selectedIds) =>
			isSelected ?
				[...selectedIds, candidateId]
			:	selectedIds.filter((selectedCandidateId) => selectedCandidateId !== candidateId),
		);
		clearErrors();
	};

	const handleContinue = ({ defaultBuyingPaymentMethodId, defaultSellingPaymentMethodId }) =>
		onContinue?.({
			paymentMethods: {
				buyingCandidateId: defaultBuyingPaymentMethodId,
				floorPlanCandidateIds,
				sellingCandidateId: defaultSellingPaymentMethodId,
			},
		});

	if (paymentMethods?.status === PAYMENT_METHOD_STATUS.processing) return <PaymentMethodsLoading />;
	if (paymentMethods?.status !== PAYMENT_METHOD_STATUS.ready) {
		return <PaymentMethodsUnavailable onBack={onBack} onRetry={onRetry} />;
	}

	return (
		<form noValidate onSubmit={handleSubmit(handleContinue)}>
			<FlexBox flexDirection="column" gap="lg">
				<FlexBox flexDirection="column" gap="sm">
					<Headline size="H2">{t('signup:auctionAccessLookup.paymentMethodsStep.title')}</Headline>
					{hasEligibleCandidates ?
						<Body>{t('signup:auctionAccessLookup.paymentMethodsStep.subTitle')}</Body>
					:	<Body>{t('signup:auctionAccessLookup.paymentMethodsStep.empty')}</Body>}
				</FlexBox>
				{hasBankAccountCandidates && (
					<>
						<FlexBox flexDirection="column" gap="sm">
							<Headline size="H5">
								{t('signup:auctionAccessLookup.paymentMethodsStep.bankAccounts')}
							</Headline>
							<Body>{t('signup:auctionAccessLookup.paymentMethodsStep.bankAccountsSubTitle')}</Body>
						</FlexBox>
						<PaymentMethodSelect
							candidates={bankAccountCandidates}
							control={control}
							disabled={!hasBankAccountCandidates}
							errors={errors}
							label={t('signup:auctionAccessLookup.paymentMethodsStep.buyingPaymentMethod')}
							name="defaultBuyingPaymentMethodId"
							onChange={clearErrors}
						/>
						<PaymentMethodSelect
							candidates={bankAccountCandidates}
							control={control}
							disabled={!hasBankAccountCandidates}
							errors={errors}
							label={t('signup:auctionAccessLookup.paymentMethodsStep.sellingPaymentMethod')}
							name="defaultSellingPaymentMethodId"
							onChange={clearErrors}
						/>
					</>
				)}

				{hasFloorPlanCandidates && (
					<FlexBox flexDirection="column" gap="sm">
						<Headline size="H5">{t('signup:auctionAccessLookup.paymentMethodsStep.floorplans')}</Headline>
						<Body>{t('signup:auctionAccessLookup.paymentMethodsStep.floorplansSubTitle')}</Body>
						{floorPlanCandidates.map((candidate) => (
							<PaymentMethodCandidate
								candidate={candidate}
								isSelected={selectedFloorPlanCandidateIds.includes(getCandidateId(candidate))}
								key={getCandidateId(candidate)}
								onChange={(event) => handleFloorPlanCandidateChange(candidate, event.target.checked)}
							/>
						))}
					</FlexBox>
				)}
				{unavailableSources.length > 0 && (
					<Message borderColor="warning" color="warning" iconColor="warning" iconName="exclamation">
						<Body>{getUnavailableSourcesMessage(unavailableSources, t)}</Body>
					</Message>
				)}
				<FormError text={error} />
				<FlexBox
					alignItems="center"
					flexDirection="row-reverse"
					gap="sm"
					justifyContent="space-between"
					width="100%"
				>
					<Button disabled={isSubmitting} type="submit">
						{t('signup:auctionAccessLookup.paymentMethodsStep.apply')}
					</Button>
					<Button disabled={isSubmitting} variant="tertiary" onClick={onBack}>
						{t('signup:actions.back')}
					</Button>
				</FlexBox>
			</FlexBox>
		</form>
	);
};

export default AuctionAccessPaymentMethod;
